import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CONTACT_ECOTIC } from '@/shared/utils/contact-ecotic';
import {
  esteScoalaOcupataCuFormularStrans,
  textPanouEditieAnterioara,
  textPanouScoalaAprobata,
  type StatusInscriereScoala,
} from '../utils/scoala-ocupata';
import { RandContactEcotic } from './RandContactEcotic';

interface ScoalaOcupataPanouProps {
  status: StatusInscriereScoala | null | undefined;
  numeProfesor: string | null | undefined;
  /** Formularul e strans: butonul «Depune totuși cererea» il deschide. Dupa apasare, panoul ramane
   *  ca informare, fara buton. */
  strans: boolean;
  onDepuneTotusi: () => void;
}

/**
 * Panoul de sub selectorul de scoala pentru o scoala «ocupata» (decizie owner, 18.09): mesajul cu
 * cele doua recomandari + contactul echipei, si formularul strans sub butonul «Depune totuși
 * cererea». Pentru `APROBATA_EDITIA_CURENTA` exista si dialogul — panoul e acolo ca cine il inchide
 * sa aiba oricum o cale de continuare, nu un formular strans fara niciun buton la vedere.
 *
 * Contactul se randeaza in doua feluri (decizie owner, 20.09): pe `EDITIE_ANTERIOARA` continua
 * propozitia, in acelasi paragraf («…echipa Ecotic : **Nume**, telefon»), pe `APROBATA_EDITIA_CURENTA`
 * ramane rând separat — acolo textul e comun cu dialogul, care il arata tot separat.
 */
export function ScoalaOcupataPanou({ status, numeProfesor, strans, onDepuneTotusi }: ScoalaOcupataPanouProps) {
  if (!esteScoalaOcupataCuFormularStrans(status)) return null;

  const aprobata = status === 'APROBATA_EDITIA_CURENTA';
  // Aceeasi sursa pentru amandoua: textul isi alege punctuatia dupa ce urmeaza dupa el, iar mai jos
  // se randeaza exact acelasi contact. Doua surse ar putea diverge intr-un colon atârnat.
  const { titlu, mesaj } = aprobata
    ? textPanouScoalaAprobata(numeProfesor)
    : textPanouEditieAnterioara(numeProfesor, CONTACT_ECOTIC !== null);

  return (
    <Alert className="border-warning bg-warning/5" data-scoala-ocupata={status}>
      <AlertDescription>
        <p className="font-semibold text-warning">ℹ️ {titlu}</p>
        {aprobata ? (
          <>
            <p className="mt-2 text-warning">{mesaj}</p>
            <RandContactEcotic contact={CONTACT_ECOTIC} className="mt-2 text-warning" />
          </>
        ) : (
          <p className="mt-2 text-warning">
            {mesaj} <RandContactEcotic contact={CONTACT_ECOTIC} prefix={null} inline />
          </p>
        )}
        {strans && (
          <Button
            type="button"
            variant="outline"
            className="touch-target mt-3 w-full sm:w-auto"
            onClick={onDepuneTotusi}
          >
            Depune totuși cererea
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
